import React, { useState, useEffect, useRef } from "react";
import {
  Wrapper,
  InfoWrapper,
  Icon,
  Name,
  InfoLeft,
  InfoRight,
  Inputs,
  InputWrapper,
  SecrecyButton,
  InfoPanelWrapper,
  InfoIcon,
} from "./styled";
import { injectIntl } from "react-intl";
import isEmpty from "lodash/isEmpty";
import Input from "../Input";
import produce from "immer";
import ValidationBar from "../common3/ValidationBar";
import Modal from "../common3/Modal";

const labels = {
  workplace: "Arbetsplats",
  phone: "Telefon",
  phoneDaytime: "Telefon dagtid",
  email: "E-post",
};

const defaultValue = {
  workplace: "",
  phone: "",
  phoneDaytime: "",
  email: "",
};

export const useIsMount = () => {
  const isMountRef = useRef(false);
  useEffect(() => {
    isMountRef.current = true;
  }, []);
  return isMountRef.current;
};

const SecondGuardian = (props) => {
  const isMounted = useIsMount();

  const {
    intl,
    builder = false,
    formData = {},
    nodes = {},
    extra = {},
    id,
    save = () => {},
    value = undefined,
    validationError = {},
    user = {},
    sampleGuardian = {},
    origin,
    disabled,
    runEffect = () => {},
  } = props;
  const {
    icon = true,
    childSelectorName = undefined,
    hideNameForSecrecyMarked = false,
    hidePersonalNumberForSecrecyMarked = false,
    hideAddressForSecrecyMarked = false,
  } = extra;
  const childSelector = Object.values(nodes).find(
    ({ extra = {} }) => extra.name === childSelectorName
  );
  const [guardian, setGuardian] = useState(builder ? sampleGuardian : {});
  const [values, setValues] = useState(value || defaultValue);
  const [displaySecrecyFields, setDisplaySecrecyFields] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // effect if childselector updates
  useEffect(() => {
    if (builder) {
      return;
    }

    if (!formData[childSelector._id] || !formData[childSelector._id].length) {
      setGuardian({});
      return;
    }

    if (formData[childSelector._id][0]?.guardians.length < 2) {
      setGuardian({});
      return;
    }

    const { guardians = [] } = formData[childSelector._id][0];

    const selectedChildGuardian = guardians.find(
      ({ personnummer }) => personnummer !== user.personnummer
    );

    if (
      selectedChildGuardian &&
      guardian.personnummer !== selectedChildGuardian.personnummer
    ) {
      setGuardian(selectedChildGuardian);
    } else if (!selectedChildGuardian) {
      setGuardian({});
    }
  }, [formData]);

  // effect
  useEffect(() => {
    if (!isMounted) {
      return;
    }

    if (builder) {
      return;
    }

    if (JSON.stringify(value) === JSON.stringify({ ...guardian, ...values })) {
      return;
    }

    save({ key: id, value: { ...guardian, ...values } });
  }, [guardian /* , isMounted */]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    if (builder) {
      return;
    }

    save({ key: id, value: { ...guardian, ...values } });
  }, [values]);

  if (!childSelector) {
    return null;
  }

  const onChange = ({ key, value }) => {
    setValues(
      produce((d) => {
        d[key] = value;
      })
    );
  };

  const confirmModalWarning = () => {
    setShowModal(false);
    setDisplaySecrecyFields(true);
    runEffect({
      source: "Log3/createLog",
      context: {
        type: "audit",
        subType: "secondGuardianSecrecyFieldsDisplayed",
        payload: { componentId: props.id, formDataId: props.formData?._id },
      },
    });
  };

  const handleSecrecyToggle = () => {
    if (displaySecrecyFields) {
      setDisplaySecrecyFields(false);
    } else {
      setShowModal(true);
    }
  };

  if (!builder && isEmpty(guardian)) {
    return null;
  }

  // if sec. guardian is selected, then we pick value from value. If not we pick from guardian. This is to ensure that we render the correct person in both admin and portal
  let {
    givenName = intl.formatMessage({ id: "SecondGuardian.givenName" }),
    familyName = intl.formatMessage({ id: "SecondGuardian.familyName" }),
    personnummer,
    address = {},
    sekretessMarkering,
    skyddadFolkbokforing,
  } = value && value.personnummer ? value : guardian;

  const { streetAddress = "", postalCode = "", addressLocality = "" } = address;

  if (origin === "admin" && !value) {
    return null;
  }

  const hasSecrecyMarking =
    sekretessMarkering === "J" || skyddadFolkbokforing === "J";
  const shouldHideSecrecyFields = hasSecrecyMarking && !displaySecrecyFields;
  const shouldHideName = hideNameForSecrecyMarked && shouldHideSecrecyFields;
  const shouldHidePersonalNumber =
    hidePersonalNumberForSecrecyMarked && shouldHideSecrecyFields;
  const shouldHideAddress =
    hideAddressForSecrecyMarked && shouldHideSecrecyFields;

  return (
    <>
      <Wrapper className="SecondGuardian">
        <InfoWrapper>
          <Name>
            {icon && <Icon className="far fa-user-friends" />}
            {shouldHideName
              ? intl.formatMessage({ id: "SecondGuardian.secondGuardian" })
              : givenName + " " + familyName}
          </Name>
          {origin === "admin" && hasSecrecyMarking && (
            <InfoRight>
              <SecrecyButton
                aria-label="Toggle secrecy info"
                onClick={handleSecrecyToggle}
              >
                <Icon
                  className={`far fa-eye${
                    displaySecrecyFields ? "-slash" : ""
                  }`}
                  style={{ margin: 0 }}
                ></Icon>
              </SecrecyButton>
            </InfoRight>
          )}
        </InfoWrapper>
        {!shouldHidePersonalNumber && (
          <InfoWrapper>
            <InfoLeft>
              {intl.formatMessage({
                id: "SecondGuardian.socialSecurityNumber",
              })}
            </InfoLeft>
            <InfoRight>{personnummer}</InfoRight>
          </InfoWrapper>
        )}
        {!shouldHideAddress && (
          <>
            <InfoWrapper>
              <InfoLeft>
                {intl.formatMessage({ id: "SecondGuardian.address" })}
              </InfoLeft>
              <InfoRight>{streetAddress}</InfoRight>
            </InfoWrapper>

            <InfoWrapper>
              <InfoLeft>
                {intl.formatMessage({ id: "SecondGuardian.zipCode" })}
              </InfoLeft>
              <InfoRight>{postalCode}</InfoRight>
            </InfoWrapper>
            <InfoWrapper>
              <InfoLeft>
                {intl.formatMessage({ id: "SecondGuardian.city" })}
              </InfoLeft>
              <InfoRight>{addressLocality}</InfoRight>
            </InfoWrapper>
          </>
        )}
        {hasSecrecyMarking && origin === "admin" && (
          <InfoWrapper>
            <InfoLeft>
              {sekretessMarkering === "J"
                ? "Sekretessmarkering"
                : "Skyddad folkbokföring"}
            </InfoLeft>
            <InfoRight>Ja</InfoRight>
          </InfoWrapper>
        )}
        <Inputs>
          <InputWrapper>
            <Input
              label="E-post"
              disabled={disabled}
              value={values.email}
              id="email"
              save={({ key, value }) => onChange({ key, value })}
              height="40"
              debounceTimer={0}
            />
          </InputWrapper>
          {Object.entries(values).map(([key, value], i) => {
            /**
             * prevent unwanted value to leak into secondguardigan
             */
            switch (key) {
              case "workplace":
              case "phone":
              case "phoneDaytime":
              case "email":
                break;
              default:
                return null;
            }

            if (!extra[key]) {
              return null;
            }

            return (
              <InputWrapper key={i}>
                <Input
                  disabled={disabled}
                  label={labels[key]}
                  value={value}
                  id={key}
                  save={({ key, value }) => onChange({ key, value })}
                  height="40"
                  debounceTimer={0}
                />
              </InputWrapper>
            );
          })}
        </Inputs>
      </Wrapper>
      {!isEmpty(validationError) && (
        <ValidationBar error={validationError.critical} />
      )}
      {showModal && (
        <Modal.Overlay>
          <Modal.Main>
            <Modal.Content>
              <InfoPanelWrapper>
                <InfoIcon className="fas fa-exclamation-triangle" />
                Denna person har sekretessmarkering eller skyddad folkbokföring.
                Är du säker på att du vill visa informationen?
              </InfoPanelWrapper>
            </Modal.Content>
            <Modal.Bottom
              cancelLabel="Avbryt"
              cancel={() => setShowModal(false)}
              primaryLabel="Visa information"
              primary={confirmModalWarning}
            />
          </Modal.Main>
        </Modal.Overlay>
      )}
    </>
  );
};

export default injectIntl(SecondGuardian);
